import { useTranslation, Trans } from 'next-i18next';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { Button } from '@components/common';

/** Types */
interface ForgotPasswordSuccessProps {
  type?: 'reset' | 'loginLink';
  emailOrPhone?: string;
  tokenLifetime?: string | number;
}

/**
 * @description Success message component for request password reset and request login link.
 * - `type`: The type of success message to display ('reset' or 'loginLink').
 * - `emailOrPhone`: The email or phone number used in the form (for a reset password success message).
 * - `tokenLifetime`: The lifetime of the token in hours (default is 2).
 */
export const ForgotPasswordSuccess = (props: ForgotPasswordSuccessProps) => {
  // Destructure props
  const {
    type = 'reset',
    emailOrPhone = '',
    tokenLifetime = 2,
  } = props;

  // Helpers
  const { t } = useTranslation('common');
  const iconClass = 'mb-4 mx-auto size-17 rounded-full bg-radial-gradient from-surface from-0% via-secondary/30 via-60% to-secondary/30 text-secondary ring-[6px] ring-secondary/10';

  // Render content
  // *************************
  return (
    <>
      {/* Success icon */}
      <CheckCircleIcon className={iconClass} />

      {/* Title */}
      <h2 className="text-2xl font-semibold tracking-tight">
        {t('global.success.msg')}
      </h2>

      {/* Message */}
      <div className="mt-2.5 text-md lg:-mx-5">
        {type === 'reset' ? (
          // Reset password: success message
          <>
            <Trans
              i18nKey="reset.password.succes.check-email"
              values={{ emailOrPhone }}
              components={{ tagName: <strong /> }}
            />
            <div className="mt-6 border-t border-dashed pt-5 text-sm">
              {t('reset.password.succes.check-email-note', { tokenLifetime })}
            </div>
          </>
        ) : (
          // Login link: success message
          <>
            {t('login.token.confirmation.body')}
            <div className="mt-6 border-t border-dashed pt-5 text-sm">
              <Trans
                i18nKey="login.token.confirmation.retry-link"
                components={{ retryUrl: <Button styling="text" size="sm" onClick={() => window.location.reload()} /> }}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};
